import React, { useEffect, useState, useCallback } from 'react';
import { Link, withRouter, useHistory } from 'react-router-dom';
import Api from '../../Services/Api';
import Alert from '../../Services/Alert';

const MODULES = [
    { key: 'dashboard',         label: 'Dashboard',              icon: 'bx-home-circle' },
    { key: 'users',             label: 'Users List',             icon: 'bx-user-circle' },
    { key: 'inactiveUsers',     label: 'Inactive Users',         icon: 'bx-user-x' },
    { key: 'deletedAccounts',   label: 'Deleted Accounts',       icon: 'bx-trash' },
    { key: 'dailyReports',      label: 'Daily Reports',          icon: 'bx-bar-chart-alt' },
    { key: 'bidReports',        label: 'Win / Place Bid Reports',icon: 'bx-receipt' },
    { key: 'bidUpdateReports',  label: 'Bid Update Reports',     icon: 'bx-revision' },
    { key: 'bulkWithdrawals',   label: 'Bulk Withdrawals',       icon: 'bx-money-withdraw' },
    { key: 'withdrawals',       label: 'Withdrawals List',       icon: 'bx-credit-card' },
    { key: 'transactions',      label: 'Market Transactions',    icon: 'bx-transfer' },
    { key: 'addMoney',          label: 'Add Money',              icon: 'bx-wallet' },
    { key: 'paymentStatus',     label: 'Payment Status',         icon: 'bx-check-circle' },
    { key: 'markets',           label: 'Markets List',           icon: 'bx-store' },
    { key: 'noticeBoard',       label: 'Notice Board',           icon: 'bx-clipboard' },
    { key: 'notifications',     label: 'Notifications',          icon: 'bx-bell' },
    { key: 'uploadApk',         label: 'Upload APK',             icon: 'bx-upload' },
    { key: 'settings',          label: 'Settings',               icon: 'bx-cog' },
    { key: 'subAdmins',         label: 'Roles & Permissions',    icon: 'bx-shield-quarter' },
    { key: 'clearData',         label: 'Clear Data',             icon: 'bx-trash-alt' },
];

export const SubAdminList = (props) => {
    const history = useHistory();
    const [subAdmins, setSubAdmins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [showPermModal, setShowPermModal] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [permissions, setPermissions] = useState({});
    const [permLoading, setPermLoading] = useState(false);
    const limit = 15;

    const defaultPermissions = () => {
        const p = {};
        MODULES.forEach(m => { p[m.key] = { read: false, write: false, delete: false }; });
        return p;
    };

    const fetchList = useCallback(() => {
        setLoading(true);
        Api.post('roles/sub-admins/list', { page, limit, search })
            .then(res => res.data)
            .then(res => {
                setLoading(false);
                if (res.status) {
                    setSubAdmins(res.data);
                    setTotal(res.total);
                } else {
                    Alert.error(res.message);
                }
            })
            .catch(err => { setLoading(false); Alert.error(err.message); });
    }, [page, search]);

    useEffect(() => { fetchList(); }, [fetchList]);

    const handleStatusChange = (id) => {
        Api.post('roles/sub-admins/status', { id })
            .then(res => res.data)
            .then(res => {
                if (res.status) {
                    Alert.success(res.message);
                    fetchList();
                } else {
                    Alert.error(res.message);
                }
            })
            .catch(err => Alert.error(err.message));
    };

    const handleDelete = (id, name) => {
        Alert.warning(`This will permanently delete "${name}". Are you sure?`).then(result => {
            if (!result.isConfirmed) return;
            Api.post('roles/sub-admins/delete', { id })
                .then(res => res.data)
                .then(res => {
                    if (res.status) {
                        Alert.success(res.message);
                        fetchList();
                    } else {
                        Alert.error(res.message);
                    }
                })
                .catch(err => Alert.error(err.message));
        });
    };

    const openPermissions = (admin) => {
        setSelectedAdmin(admin);
        const mergedPerms = Object.assign({}, defaultPermissions(), admin.permissions || {});
        setPermissions(mergedPerms);
        setShowPermModal(true);
    };

    const handlePermChange = (module, action, value) => {
        setPermissions(prev => ({
            ...prev,
            [module]: { ...prev[module], [action]: value }
        }));
    };

    const handleSelectAll = (module, checked) => {
        setPermissions(prev => ({
            ...prev,
            [module]: { read: checked, write: checked, delete: checked }
        }));
    };

    const savePermissions = () => {
        setPermLoading(true);
        Api.post('roles/sub-admins/update-permissions', { id: selectedAdmin._id, permissions })
            .then(res => res.data)
            .then(res => {
                setPermLoading(false);
                if (res.status) {
                    Alert.success(res.message);
                    setShowPermModal(false);
                    fetchList();
                } else {
                    Alert.error(res.message);
                }
            })
            .catch(err => { setPermLoading(false); Alert.error(err.message); });
    };

    const totalPages = Math.ceil(total / limit);

    return (
        <section className="main-content">
        <div className="page-content">
            <div className="container-fluid">
                {/* Page Header */}
                <div className="row">
                    <div className="col-12">
                        <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                            <h4 className="mb-sm-0 font-size-18">
                                <i className="bx bx-shield-quarter me-2" style={{ color: '#556ee6' }}></i>
                                Users & Role Management
                            </h4>
                            <div className="page-title-right">
                                <ol className="breadcrumb m-0">
                                    <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                                    <li className="breadcrumb-item active">Sub-Admins</li>
                                </ol>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-12">
                        <div className="card" style={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(85,110,230,0.12)' }}>
                            <div className="card-header" style={{ background: 'linear-gradient(135deg, #556ee6 0%, #6f42c1 100%)', borderRadius: '12px 12px 0 0', padding: '18px 24px' }}>
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <div>
                                        <h5 className="mb-0 text-white">
                                            <i className="bx bx-user-check me-2"></i>Sub-Admins
                                        </h5>
                                        <small className="text-white-50">Manage sub-admin accounts and their permissions</small>
                                    </div>
                                    <Link to="/roles/sub-admins/add" className="btn btn-light btn-sm">
                                        <i className="bx bx-plus-circle me-1"></i> Add Sub-Admin
                                    </Link>
                                </div>
                            </div>
                            <div className="card-body">
                                {/* Search Bar */}
                                <div className="row mb-3">
                                    <div className="col-md-4">
                                        <div className="input-group">
                                            <span className="input-group-text"><i className="bx bx-search"></i></span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search by name or email..."
                                                value={search}
                                                onChange={e => { setSearch(e.target.value); setPage(1); }}
                                                id="sub-admin-search"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-8 text-end">
                                        <span className="text-muted">Total: <strong>{total}</strong> sub-admins</span>
                                    </div>
                                </div>

                                {/* Table */}
                                <div className="table-responsive">
                                    <table className="table table-hover table-striped align-middle mb-0">
                                        <thead style={{ background: '#f8f9fa' }}>
                                            <tr>
                                                <th>#</th>
                                                <th>Name</th>
                                                <th>Email</th>
                                                <th>Role</th>
                                                <th>Permissions</th>
                                                <th>Status</th>
                                                <th>Created At</th>
                                                <th className="text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr><td colSpan="8" className="text-center py-4">
                                                    <div className="spinner-border text-primary" role="status"></div>
                                                </td></tr>
                                            ) : subAdmins.length === 0 ? (
                                                <tr><td colSpan="8" className="text-center py-4 text-muted">
                                                    <i className="bx bx-user-x font-size-24 d-block mb-2"></i>
                                                    No sub-admins found
                                                </td></tr>
                                            ) : subAdmins.map((sa, idx) => {
                                                const perms = sa.permissions || {};
                                                const activeModules = MODULES.filter(m => {
                                                    const mp = perms[m.key] || {};
                                                    return mp.read || mp.write || mp.delete;
                                                });
                                                return (
                                                    <tr key={sa._id}>
                                                        <td>{(page - 1) * limit + idx + 1}</td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <div className="avatar-xs me-2">
                                                                    <span className="avatar-title rounded-circle font-size-12" style={{ background: '#556ee6' }}>
                                                                        {sa.firstname?.charAt(0)}{sa.lastname?.charAt(0)}
                                                                    </span>
                                                                </div>
                                                                <div>
                                                                    <strong>{sa.firstname} {sa.lastname}</strong>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>{sa.email}</td>
                                                        <td>
                                                            {sa.role === 'support_agent' && <span className="badge" style={{ background: '#0dcaf0', color: '#000' }}>🎧 Support Agent</span>}
                                                            {sa.role === 'supervisor' && <span className="badge" style={{ background: '#6f42c1' }}>👁️ Supervisor</span>}
                                                            {sa.role === 'subadmin' && <span className="badge" style={{ background: '#556ee6' }}>🛡️ Sub Admin</span>}
                                                        </td>
                                                        <td>
                                                            {activeModules.length === 0 ? (
                                                                <span className="badge bg-secondary">No Permissions</span>
                                                            ) : (
                                                                <div className="d-flex flex-wrap gap-1">
                                                                    {activeModules.slice(0, 3).map(m => (
                                                                        <span key={m.key} className="badge" style={{ background: '#ebedff', color: '#556ee6', fontSize: '10px' }}>
                                                                            {m.label}
                                                                        </span>
                                                                    ))}
                                                                    {activeModules.length > 3 && (
                                                                        <span className="badge bg-light text-dark" style={{ fontSize: '10px' }}>
                                                                            +{activeModules.length - 3} more
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${sa.status === 1 ? 'bg-success' : 'bg-danger'}`}>
                                                                {sa.status === 1 ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td>{new Date(sa.createdAt).toLocaleDateString()}</td>
                                                        <td className="text-center">
                                                            <div className="btn-group" role="group">
                                                                <button
                                                                    className="btn btn-sm btn-outline-info"
                                                                    title="Manage Permissions"
                                                                    onClick={() => openPermissions(sa)}
                                                                    id={`perm-btn-${sa._id}`}
                                                                >
                                                                    <i className="bx bx-shield"></i>
                                                                </button>
                                                                <Link
                                                                    to={`/roles/sub-admins/edit/${sa._id}`}
                                                                    className="btn btn-sm btn-outline-primary"
                                                                    title="Edit"
                                                                    id={`edit-btn-${sa._id}`}
                                                                >
                                                                    <i className="bx bx-edit"></i>
                                                                </Link>
                                                                <button
                                                                    className={`btn btn-sm ${sa.status === 1 ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                                                    title={sa.status === 1 ? 'Deactivate' : 'Activate'}
                                                                    onClick={() => handleStatusChange(sa._id)}
                                                                    id={`status-btn-${sa._id}`}
                                                                >
                                                                    <i className={`bx ${sa.status === 1 ? 'bx-block' : 'bx-check-circle'}`}></i>
                                                                </button>
                                                                <button
                                                                    className="btn btn-sm btn-outline-danger"
                                                                    title="Delete"
                                                                    onClick={() => handleDelete(sa._id, `${sa.firstname} ${sa.lastname}`)}
                                                                    id={`del-btn-${sa._id}`}
                                                                >
                                                                    <i className="bx bx-trash"></i>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <span className="text-muted">Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}</span>
                                        <nav>
                                            <ul className="pagination mb-0">
                                                <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                                    <button className="page-link" onClick={() => setPage(p => p - 1)}>‹</button>
                                                </li>
                                                {[...Array(totalPages)].map((_, i) => (
                                                    <li key={i} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                                                        <button className="page-link" onClick={() => setPage(i + 1)}>{i + 1}</button>
                                                    </li>
                                                ))}
                                                <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                                                    <button className="page-link" onClick={() => setPage(p => p + 1)}>›</button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Permissions Modal */}
            {showPermModal && selectedAdmin && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content" style={{ borderRadius: '12px' }}>
                            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #556ee6 0%, #6f42c1 100%)' }}>
                                <div>
                                    <h5 className="modal-title text-white">
                                        <i className="bx bx-shield-quarter me-2"></i>Permission Settings
                                    </h5>
                                    <small className="text-white-50">
                                        {selectedAdmin.firstname} {selectedAdmin.lastname} — {selectedAdmin.email}
                                    </small>
                                </div>
                                <button className="btn-close btn-close-white" onClick={() => setShowPermModal(false)}></button>
                            </div>
                            <div className="modal-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-bordered mb-0">
                                        <thead style={{ background: '#f8f9fa', position: 'sticky', top: 0 }}>
                                            <tr>
                                                <th style={{ width: '35%' }}>Module</th>
                                                <th className="text-center">Select All</th>
                                                <th className="text-center">
                                                    <i className="bx bx-show me-1 text-primary"></i>Read
                                                </th>
                                                <th className="text-center">
                                                    <i className="bx bx-edit me-1 text-warning"></i>Write
                                                </th>
                                                <th className="text-center">
                                                    <i className="bx bx-trash me-1 text-danger"></i>Delete
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {MODULES.map(mod => {
                                                const mp = permissions[mod.key] || { read: false, write: false, delete: false };
                                                const allChecked = mp.read && mp.write && mp.delete;
                                                return (
                                                    <tr key={mod.key} style={{ transition: 'background 0.2s' }}
                                                        className={allChecked ? 'table-primary' : ''}>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <i className="bx bx-layer me-2 text-primary"></i>
                                                                <strong>{mod.label}</strong>
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                            <div className="form-check d-flex justify-content-center mb-0">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    checked={allChecked}
                                                                    id={`all-${mod.key}`}
                                                                    onChange={e => handleSelectAll(mod.key, e.target.checked)}
                                                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                                                />
                                                            </div>
                                                        </td>
                                                        {['read', 'write', 'delete'].map(action => (
                                                            <td key={action} className="text-center">
                                                                <div className="form-check d-flex justify-content-center mb-0">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        checked={mp[action] || false}
                                                                        id={`${mod.key}-${action}`}
                                                                        onChange={e => handlePermChange(mod.key, action, e.target.checked)}
                                                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                                                    />
                                                                </div>
                                                            </td>
                                                        ))}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowPermModal(false)}>Cancel</button>
                                <button
                                    className="btn btn-primary"
                                    onClick={savePermissions}
                                    disabled={permLoading}
                                    id="save-permissions-btn"
                                >
                                    {permLoading ? (
                                        <span className="spinner-border spinner-border-sm me-1"></span>
                                    ) : (
                                        <i className="bx bx-save me-1"></i>
                                    )}
                                    Save Permissions
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
        </section>
    );
};

export default withRouter(SubAdminList);
